import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, VolumeX, Thermometer, Droplets, Music } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { useSEO } from '../../hooks/useSEO';
// Images
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/bwQjjTaBFPGmsVMM5LvtNo/_DSC8285.webp';
const DETAIL_IMAGE_1 = 'https://cdn.magicpatterns.com/uploads/pKPzBNwMUWtZUzQjDCs91d/6842e88f2f523e985c5fa1a4__DSC8349-2.jpg';
const DETAIL_IMAGE_2 = 'https://cdn.magicpatterns.com/uploads/3xL1jkjmSyiggyKFQmbzxH/_DSC8261.webp';
export function SilentTravelPage() {
  useSEO({
    title: 'Silent Travel — Whisper-Quiet Campervan Journeys',
    description: 'Experience Silent Travel by Robeta. Advanced Armaflex insulation and precision fitment deliver the quietest ride in the camper van industry.'
  });
  return <main className="bg-white min-h-screen" data-id="element-1084">
      {/* 1. FULL-SCREEN HERO */}
      <section className="relative min-h-screen w-full flex flex-col justify-between overflow-hidden" data-id="element-1085">
        <div className="absolute inset-0 z-0" data-id="element-1086">
          <img src={HERO_IMAGE} alt="Silent Travel Hero" className="w-full h-full object-cover" data-id="element-1087" />
          <div className="absolute inset-0 bg-black/40" data-id="element-1088" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" data-id="element-1089" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10 pt-32" data-id="element-1090">
          <Breadcrumbs items={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Robeta Difference',
          href: '/robeta-difference'
        }, {
          label: 'Silent Travel'
        }]} variant="light" data-id="element-1091" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10 pb-24 md:pb-32" data-id="element-1092">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="max-w-4xl" data-id="element-1093">
            <span className="text-robeta-copper text-sm font-bold uppercase tracking-wide-editorial block mb-6" data-id="element-1094">
              The Robeta Standard
            </span>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white uppercase tracking-editorial leading-[0.9] mb-8" data-id="element-1095">
              Experience
              <br data-id="element-1096" />
              Silent Travel
            </h1>
            <p className="text-xl md:text-2xl font-light text-white/80 max-w-2xl leading-relaxed" data-id="element-1097">
              Silence is not just the absence of noise. It is the presence of
              peace.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. INTRO SECTION */}
      <section className="py-24 md:py-32 bg-white" data-id="element-1098">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1099">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1100">
            <h2 className="text-3xl md:text-5xl font-light text-robeta-black leading-tight mb-8" data-id="element-1101">
              "We believe the journey should be as{' '}
              <span className="italic font-serif text-robeta-copper" data-id="element-1102">
                restorative
              </span>{' '}
              as the destination itself."
            </h2>
            <p className="text-lg text-robeta-black/60 font-light leading-relaxed max-w-2xl mx-auto" data-id="element-1103">
              Most camper vans are noisy. The rattle of cabinetry, the hum of
              the road, and the sound of wind can turn a long drive into an
              exhausting ordeal. At Robeta, we engineer silence into the very
              chassis of our vehicles, creating a sanctuary that remains
              tranquil at 100 km/h.
            </p>
          </div>
        </div>
      </section>

      {/* 3. DEEP DIVE 1: ARMAFLEX */}
      <section className="py-20 md:py-32 bg-robeta-mist/10" data-id="element-1104">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1105">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1106">
            <div className="order-2 lg:order-1" data-id="element-1107">
              <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-4" data-id="element-1108">
                Advanced Insulation
              </span>
              <h3 className="text-3xl md:text-4xl font-bold text-robeta-black uppercase tracking-editorial mb-6" data-id="element-1109">
                The Armaflex Advantage
              </h3>
              <div className="space-y-6 text-robeta-black/70 font-light leading-relaxed" data-id="element-1110">
                <p data-id="element-1111">
                  Every Robeta camper van is wrapped in a cocoon of{' '}
                  <strong data-id="element-1112">25mm Armaflex</strong> insulation. This premium,
                  closed-cell elastomeric foam is the gold standard in thermal
                  and acoustic engineering, typically reserved for industrial
                  applications and high-end marine vessels.
                </p>
                <p data-id="element-1113">
                  Unlike the cheap styrofoam or fiberglass wool used by
                  mass-market manufacturers, Armaflex does not settle, degrade,
                  or absorb moisture over time. It is applied seamlessly to the
                  vehicle's metal skin, eliminating thermal bridges and
                  dampening the resonant frequency of the chassis itself.
                </p>
                <p data-id="element-1114">
                  The result is a dramatic reduction in road noise—up to 50%
                  quieter than competitors—and a cabin that maintains its
                  temperature, whether you're parked in the scorching sun or the
                  freezing alps.
                </p>
              </div>
            </div>
            <div className="order-1 lg:order-2 relative aspect-[4/3] overflow-hidden" data-id="element-1115">
              <img src={DETAIL_IMAGE_1} alt="Armaflex Insulation Detail" className="absolute inset-0 w-full h-full object-cover" data-id="element-1116" />
            </div>
          </div>
        </div>
      </section>

      {/* 4. DEEP DIVE 2: PRECISION FITMENT */}
      <section className="py-20 md:py-32 bg-white" data-id="element-1117">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1118">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1119">
            <div className="relative aspect-[4/3] overflow-hidden" data-id="element-1120">
              <img src={DETAIL_IMAGE_2} alt="Precision Interior Fitment" className="absolute inset-0 w-full h-full object-cover" data-id="element-1121" />
            </div>
            <div data-id="element-1122">
              <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-4" data-id="element-1123">
                Engineering
              </span>
              <h3 className="text-3xl md:text-4xl font-bold text-robeta-black uppercase tracking-editorial mb-6" data-id="element-1124">
                Eliminating the Rattle
              </h3>
              <div className="space-y-6 text-robeta-black/70 font-light leading-relaxed" data-id="element-1125">
                <p data-id="element-1126">
                  Insulation is only half the story. The other half is how the
                  furniture fits together. In a moving vehicle, every joint is a
                  potential source of noise.
                </p>
                <p data-id="element-1127">
                  Our "One Van, One Craftsman" philosophy ensures that every
                  cabinet, panel, and drawer is hand-fitted with obsessive
                  attention to detail. We use felt strips between contact
                  points, soft-close mechanisms on all drawers, and a unique
                  structural mounting system that allows the furniture to flex
                  slightly with the chassis rather than fighting against it.
                </p>
                <p data-id="element-1128">
                  This eliminates the squeaks, rattles, and groans that plague
                  typical RVs. When you drive a Robeta, you hear your music,
                  your conversation, or simply the silence of the road.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. KEY BENEFITS GRID */}
      <section className="py-24 bg-robeta-black text-white" data-id="element-1129">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1130">
          <div className="text-center mb-16" data-id="element-1131">
            <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-editorial mb-4" data-id="element-1132">
              The Sound of Quality
            </h2>
            <div className="w-24 h-1 bg-robeta-copper mx-auto" data-id="element-1133" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1134">
            {[{
            icon: VolumeX,
            title: 'Acoustic Dampening',
            desc: 'Significantly reduces road noise, wind noise, and exterior disturbances for a peaceful ride.'
          }, {
            icon: Thermometer,
            title: 'Thermal Regulation',
            desc: 'Keeps the interior cool in summer and warm in winter, reducing reliance on AC and heating.'
          }, {
            icon: Droplets,
            title: 'Moisture Barrier',
            desc: 'Closed-cell structure prevents condensation and mold, protecting your health and the vehicle.'
          }, {
            icon: Music,
            title: 'Audio Clarity',
            desc: 'A quieter cabin means better acoustics for your music and easier conversation while driving.'
          }].map((item, i) => <div key={i} className="bg-white/5 p-8 border border-white/10 hover:border-robeta-copper/50 transition-colors" data-id="element-1135">
                <item.icon className="w-10 h-10 text-robeta-copper mb-6" data-id="element-1136" />
                <h3 className="text-xl font-bold uppercase tracking-wide mb-4" data-id="element-1137">
                  {item.title}
                </h3>
                <p className="text-white/60 font-light leading-relaxed text-sm" data-id="element-1138">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. NEXT PILLAR NAV */}
      <section className="border-t border-robeta-black/10 bg-white" data-id="element-1139">
        <div className="container mx-auto px-6 md:px-12 py-16 md:py-24" data-id="element-1140">
          <div className="flex flex-col items-center text-center" data-id="element-1141">
            <span className="text-robeta-black/40 text-xs font-bold uppercase tracking-widest mb-4" data-id="element-1142">
              Next Pillar
            </span>
            <Link to="/robeta-difference/air-furniture" className="group flex flex-col items-center" data-id="element-1143">
              <h2 className="text-4xl md:text-6xl font-bold text-robeta-black uppercase tracking-editorial mb-6 group-hover:text-robeta-copper transition-colors" data-id="element-1144">
                Air Furniture Technology™
              </h2>
              <div className="flex items-center text-sm font-bold uppercase tracking-widest text-robeta-black group-hover:text-robeta-copper transition-colors" data-id="element-1145">
                Discover Innovation <ArrowRight className="ml-2 w-4 h-4" data-id="element-1146" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* 7. CTA SECTION */}
      <section className="py-28 md:py-36 bg-robeta-mist/20 text-center" data-id="element-1147">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1148">
          <h2 className="text-4xl md:text-5xl font-bold text-robeta-black uppercase tracking-editorial mb-8" data-id="element-1149">
            Hear the Difference Yourself
          </h2>
          <p className="text-lg text-robeta-black/60 font-light max-w-2xl mx-auto mb-12" data-id="element-1150">
            Visit a dealer to experience the quietest ride in the industry.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-6" data-id="element-1151">
            <Link to="/dealer-network" className="px-10 py-4 bg-robeta-black text-white font-bold uppercase tracking-widest hover:bg-robeta-copper transition-colors min-w-[200px]" data-id="element-1152">
              Find a Dealer
            </Link>
            <Link to="/models" className="px-10 py-4 border border-robeta-black text-robeta-black font-bold uppercase tracking-widest hover:bg-white transition-colors min-w-[200px]" data-id="element-1153">
              View Models
            </Link>
          </div>
        </div>
      </section>
    </main>;
}